import { ChangeDetectionStrategy, Component, forwardRef, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { ampmOptions, hoursOptions, monthDaysOptions, weekDaysOptions } from './constants';
import { filter } from 'rxjs/operators';
import { OptionInterface } from './models';
import { AmPmEnum, FormKeysEnum, FrequencyTypesEnum } from './enums';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/**
 * (*  *  *  *  *  *) -> ('seconds', 'minutes', 'hours', 'day of the month', 'month', 'day of the week')
 */

@UntilDestroy()
@Component({
    selector: 'app-legacy-cron-job',
    templateUrl: './legacy-cron-job.component.html',
    styleUrls: ['./legacy-cron-job.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LegacyCronJobComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => LegacyCronJobComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class LegacyCronJobComponent implements ControlValueAccessor, Validator, OnInit {
    form: FormGroup;
    selectedFrequencyControl: FormControl = new FormControl('');

    readonly formKeysEnum: typeof FormKeysEnum = FormKeysEnum;
    readonly frequencyTypesEnum: typeof FrequencyTypesEnum = FrequencyTypesEnum;
    readonly weekDaysOptions: OptionInterface[] = weekDaysOptions;
    readonly monthDaysOptions: number[] = monthDaysOptions;
    readonly hoursOptions: number[] = hoursOptions;
    readonly ampmOptions: string[] = ampmOptions;

    private onChanged: (value: string) => void;
    private onTouched: () => void;

    constructor(private readonly formBuilder: FormBuilder) {
        this.buildForm();
    }

    ngOnInit(): void {
        this.form.valueChanges
        .pipe(
          filter(() => Boolean(this.onChanged)),
          untilDestroyed(this),
        )
        .subscribe((value) => {
            let hours: number = value.hours;
            if (hours && value.ampm === AmPmEnum.PM) {
                hours = (+hours !== 12 ? +hours + 12 : +hours - 12);
            }

            const cHours: string = (hours >= 0 && hours !== null) ? `${hours}` : '*';
            const cMinutes: string = (hours >= 0 && hours !== null) ? '0' : '*';
            const dom: string = value.monthDay ? `${value.monthDay}` : '*';
            const dow: string = (value.weekDay >= 0 && value.weekDay !== null) ? `${value.weekDay}` : '*';
            this.onChanged(`* ${cMinutes} ${cHours} ${dom} * ${dow}`);
            this.onTouched();
        });

        this.selectedFrequencyControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.onFrequencyChange();
        });
    }

    writeValue(value: string): void {
        if (!value && !value?.length) {
            this.selectedFrequencyControl.setValue(null);
            this.form.setValue({
                [FormKeysEnum.Hours]: 9,
                [FormKeysEnum.Ampm]: AmPmEnum.AM,
            }, { emitEvent: false });
            return;
        }
        const [seconds, minutes, hours, dom, month, dow] = value.split(' ');
        const selectedType: FrequencyTypesEnum = dom !== '*' ? FrequencyTypesEnum.MONTHLY : dow !== '*' ? FrequencyTypesEnum.WEEKLY : FrequencyTypesEnum.DAILY;
        const hoursNum: number = +hours;
        this.selectedFrequencyControl.setValue(selectedType);
        this.form.patchValue({
            [FormKeysEnum.Hours]: hoursNum === 0 ? hoursNum + 12 : hoursNum > 12 ? hoursNum - 12 : hoursNum,
            [FormKeysEnum.Ampm]: hoursNum > 12 || hoursNum === 0 ? AmPmEnum.PM : AmPmEnum.AM,
            [FormKeysEnum.MonthDay]: +dom,
            [FormKeysEnum.WeekDay]: +dow,
        }, { emitEvent: false });
    }

    registerOnChange(fn: (color: string) => void): void {
        this.onChanged = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return this.form.valid ? null : { cronJob: { valid: false } };
    }

    private onFrequencyChange(): void {
        const selectedFrequencyType: FrequencyTypesEnum = this.selectedFrequencyControl.value;

        switch (selectedFrequencyType) {
            case FrequencyTypesEnum.DAILY:
                this.form.removeControl(FormKeysEnum.MonthDay);
                this.form.removeControl(FormKeysEnum.WeekDay);
                break;
            case FrequencyTypesEnum.WEEKLY:
                this.form.addControl(FormKeysEnum.WeekDay, this.formBuilder.control(1, Validators.required));
                this.form.removeControl(FormKeysEnum.MonthDay);
                break;
            case FrequencyTypesEnum.MONTHLY:
                this.form.addControl(FormKeysEnum.MonthDay, this.formBuilder.control(1, Validators.required));
                this.form.removeControl(FormKeysEnum.WeekDay);
                break;
            default:
                this.form.removeControl(FormKeysEnum.MonthDay);
                this.form.removeControl(FormKeysEnum.WeekDay);
                break;
        }
    }

    private buildForm(): void {
        this.form = this.formBuilder.group({
            [FormKeysEnum.Hours]: [9, Validators.required],
            [FormKeysEnum.Ampm]: [AmPmEnum.AM, Validators.required],
        });
    }
}
